<template>
  <div class="promo-design-group background-opt-input">
    <v-checkbox
      v-if="!isMobile"
      v-model="backgroundImageEnabled"
      label="Use background image"
      class="background-img-checkbox"
      hideDetails
      style="padding-bottom: 10px"
    />
    <div class="bgimg-setup-div">
      <v-radio-group
        v-model="bgImageIsUrl"
        hideDetails
        inline
        :aria-label="`${isMobile ? 'Mobile b' : 'B'}ackground image input method`"
      >
        <v-radio label="Upload Image File" value="false" />
        <v-radio label="Use Image URL" value="true" />
      </v-radio-group>
      <div class="upload-bgimg-wrapper">
        <!-- URL Input Mode -->
        <template v-if="bgImageIsUrl === 'true'">
          <v-textarea
            v-model="bgImage"
            variant="outlined"
            density="compact"
            :hideDetails="!urlErrorMessage"
            :label="`${isMobile ? 'Mobile ' : ''}Background Image URL`"
            :name="`${isMobile ? 'mobile-' : ''}background-image-url`"
            persistentPlaceholder
            :rules="[urlValidationRule]"
            :errorMessages="urlErrorMessage"
          ></v-textarea>
        </template>

        <!-- File Upload Mode -->
        <div v-else>
          <v-btn
            variant="flat"
            color="accent"
            class="upload-image-btn"
            :disabled="uploadDisabled"
            @click="
              triggerUploadBgImageFromInput(isMobile ? 'bgImageMobileWebInput' : 'bgImageInput')
            "
          >
            <v-icon size="14" start>fa:fas fa-upload</v-icon>
            Upload
            <template v-if="!isNewDevicePrompt">
              {{ isMobile ? "mobile web" : "desktop" }}
            </template>
            background
          </v-btn>
          <RfImageFileInfo :image="bgImage" :filename="bgImageFilename" />
          <input
            v-if="isMobile"
            ref="bgImageMobileWebInput"
            type="file"
            style="display: none"
            accept="image/*"
            @change="uploadBgImageFromInput('mobileWeb')"
          />
          <input
            v-else
            ref="bgImageInput"
            type="file"
            style="display: none"
            accept="image/*"
            @change="uploadBgImageFromInput('desktop')"
          />
        </div>

        <!-- Shared Helper Text -->
        <div v-if="imgDimensionsText" class="upload-img-helper">
          {{ imgDimensionsText }}
        </div>
      </div>
      <div v-if="isNotEmailPromo && !isMobile" class="pr-bg-image-options-row">
        <v-select
          v-if="!isDeviceInterstitial"
          v-model="actions.rf_retention_img_align"
          variant="outlined"
          hideDetails
          density="compact"
          persistentPlaceholder
          label="Position"
          :items="bgImageAlignOptions"
          itemTitle="text"
        />
        <v-select
          v-if="isHorizontalVerticalTile"
          v-model="actions.rf_settings_tile_is_responsive"
          variant="outlined"
          hideDetails
          density="compact"
          persistentPlaceholder
          label="Adjust size"
          :disabled="!backgroundImageEnabled"
          :items="bgImageSize"
          itemTitle="text"
        />
        <v-text-field
          v-model.trim="actions.rf_settings_bg_image_color"
          variant="outlined"
          persistentPlaceholder
          density="compact"
          hideDetails
          label="Bg Color"
          name="bg-image-color"
          placeholder="e.g.: #336699"
          style="padding: 5px 0 2px"
          @blur="actions.rf_settings_bg_image_color = extendHex(actions.rf_settings_bg_image_color)"
        ></v-text-field>
      </div>
    </div>
  </div>
</template>

<script>
import UploadImageMixin from "@/utils/UploadImageMixin";
import LanguagePreviewMixin from "@/utils/LanguagePreviewMixin";
import { isEmail } from "@/utils/prompts/promptHelpers";
import { DefaultDeviceOptions } from "@/utils/constants/DevicesConstants";
import { useAppsStore } from "@/pinia/appsStore";
import RfImageFileInfo from "./RfImageFileInfo.vue";
import { PromptTypes } from "@/utils/constants/PromoTypesConstants";
import { deviceDimensions } from "@/utils/promptDimensionUtils";
import { extendHex } from "@/utils/colorHelpers";

export default {
  name: "RfRetentionImageUpload",
  components: { RfImageFileInfo },
  mixins: [UploadImageMixin, LanguagePreviewMixin],
  props: {
    isMobile: { type: Boolean, default: false },
    actions: Object,
    model: Object,
  },
  setup: () => ({
    extendHex,
    appsStore: useAppsStore(),
    bgImageSize: [
      { value: "cover", text: "Fill" },
      { value: "contain", text: "Fit" },
      { value: "auto", text: "Actual" },
    ],
    bgImageAlignOptions: [
      { value: "left", text: "Left" },
      { value: "right", text: "Right" },
    ],
  }),
  data() {
    return {
      rf_settings_bg_image: this.model.id ? this.model.actions.rf_settings_bg_image : null,
      rf_settings_mobile_bg_image: this.model.id
        ? this.model.actions.rf_settings_mobile_bg_image
        : null,
      backgroundImageEnabled: this.model.id ? !!this.model.actions.rf_settings_bg_image : true,
    };
  },
  computed: {
    bgImageIsUrl: {
      get() {
        return this.isMobile
          ? this.actions.rf_settings_mobile_bg_image_is_url || "false"
          : this.actions.rf_settings_bg_image_is_url || "false";
      },
      set(value) {
        if (this.isMobile) {
          this.actions.rf_settings_mobile_bg_image_is_url = value;
        } else {
          this.actions.rf_settings_bg_image_is_url = value;
        }
      },
    },
    bgImage: {
      get() {
        return this.isMobile
          ? this.actions.rf_settings_mobile_bg_image || "false"
          : this.actions.rf_settings_bg_image || "false";
      },
      set(value) {
        if (this.isMobile) {
          this.actions.rf_settings_mobile_bg_image = value;
        } else {
          this.actions.rf_settings_bg_image = value;
        }
      },
    },
    bgImageFilename: {
      get() {
        return this.isMobile
          ? this.actions.rf_settings_mobile_bg_image_filename || "false"
          : this.actions.rf_settings_bg_image_filename || "false";
      },
      set(value) {
        if (this.isMobile) {
          this.actions.rf_settings_mobile_bg_image_filename = value;
        } else {
          this.actions.rf_settings_bg_image_filename = value;
        }
      },
    },
    isAndroidDevice() {
      return DefaultDeviceOptions.android_os.value === this.model.device_type;
    },
    isAndroidTvDevice() {
      return DefaultDeviceOptions.android_tv.value === this.model.device_type;
    },
    isIosDevice() {
      return DefaultDeviceOptions.ios.value === this.model.device_type;
    },
    isTvosDevice() {
      return DefaultDeviceOptions.tv_os.value === this.model.device_type;
    },
    isNewAndroid() {
      return (
        this.appsStore.app?.flags?.new_android_editor &&
        (this.isAndroidDevice || this.isAndroidTvDevice)
      );
    },
    isNewAppleEditor() {
      return (
        !this.appsStore.app?.flags?.legacy_apple_editor && (this.isIosDevice || this.isTvosDevice)
      );
    },
    isInterstitial() {
      return this.model.path_type === PromptTypes.interstitial.value;
    },
    isPopUp() {
      return this.model.path_type === PromptTypes.retention_modal.value;
    },
    isNewDevicePrompt() {
      return this.isPopUp && (this.isNewAndroid || this.isNewAppleEditor);
    },
    isNotEmailPromo() {
      return !isEmail(this.model);
    },
    backgroundImageOptionEnabled() {
      if (this.isInterstitial) return false;
      return this.bgImage;
    },
    isHorizontalVerticalTile() {
      return [
        PromptTypes.horizontal.value,
        PromptTypes.vertical.value,
        PromptTypes.tile.value,
        PromptTypes.interstitial.value,
      ].includes(this.model.path_type);
    },
    uploadDisabled() {
      if (!this.backgroundImageEnabled) return true;
      return this.currentTranslations.hasOwnProperty(
        this.isMobile ? "rf_settings_mobile_bg_image" : "rf_settings_bg_image",
      );
    },
    isDeviceInterstitial() {
      return (
        this.isInterstitial && (this.isIosDevice || this.isAndroidDevice || this.isAndroidTvDevice)
      );
    },
    imgDimensionsText() {
      const dims = deviceDimensions(this.model.device_type, this.model.path_type);
      return dims ? `${dims} recommended` : "";
    },
    hasImageUrl() {
      return this.bgImage?.startsWith("http");
    },
    urlErrorMessage() {
      if (this.bgImageIsUrl !== "true" || !this.bgImage) {
        return null;
      }

      const result = this.urlValidationRule(this.bgImage);
      return result === true ? null : result;
    },
  },
  watch: {
    backgroundImageEnabled(to) {
      this.actions.rf_settings_bg_image = to ? this.rf_settings_bg_image : null;
      this.actions.rf_settings_mobile_bg_image = to ? this.rf_settings_mobile_bg_image : null;
      this.$emit("forceParentRender");
    },
  },
  mounted() {
    // Set default value for rf_settings_bg_image_is_url/rf_settings_mobile_bg_image_is_url if not already set
    if (this.bgImageIsUrl === undefined || this.bgImageIsUrl === null) {
      this.bgImageIsUrl = "false";
    }
  },
  methods: {
    urlValidationRule(value) {
      if (!value || value.trim() === "") return true; // Allow empty

      // Reject data URLs
      if (value.toLowerCase().startsWith("data:")) {
        return "Data URLs are not allowed. Please use a web URL (e.g., https://example.com/image.jpg)";
      }

      try {
        new URL(value);
        return true;
      } catch {
        return "Please enter a valid URL (e.g., https://example.com/image.jpg)";
      }
    },
    triggerUploadBgImageFromInput(displayType) {
      if (displayType == "bgImageInput") {
        this.$refs.bgImageInput.click();
      } else {
        this.$refs.bgImageMobileWebInput.click();
      }
    },
  },
};
</script>
